import usePaginationStateHooks from 'hooks/CustomHooks/usePaginationStateHooks';
import { useEffect, useState } from 'react';
import { useLoginStore } from 'store/useLoginDataStore';
import { BorderColor } from 'styles/Global/colorSemantic';
import { PageTitle, RowDivider } from 'styles/Global/commonStyles';
import { DiagnosisLayout, FilterWrapper, HospitalPageLabel } from 'styles/Global/hospitalStyles';
import { useLoadingStore } from 'store/useLoadingStore';
import { AdminManageHospitalConstants } from 'constants/Admin/AdminManageHospitalPageConstants';
import { adminManageHospitalPageApi } from 'api/Admin/adminManageHospitalPageApi';
import AdminManageHospitalPageFilter from './AdminManageHospitalPageFilter';
import AdminManageHospitalPagination from './AdminManageHospitalPagination';
import { GetHospitalListApiType } from 'models/DCKAdmin/AdminManageHospitalPageTypes';
import { AdminInnerLayout } from 'styles/Global/Admin/adminCommonStyles';
import styled from 'styled-components';
import AdminManageHospitalDetail from './AdminManageHospitalDetail';
import { PositiveButton } from 'components/Buttons/VariantsButtons';

const AdminManageHospitalPage = () => {
    const { loginData } = useLoginStore();
    const { setLoading } = useLoadingStore();

    const [hospitalData, setHospitalData] = useState<GetHospitalListApiType[]>();
    const [searchValue, setSearchValue] = useState<string>('');
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [selectedHospital, setSelectedHospital] = useState<GetHospitalListApiType | null>(null);
    const [editedHospital, setEditedHospital] = useState<GetHospitalListApiType | null>(null);
    const [isNewHospital, setIsNewHospital] = useState<boolean>(false);

    const { setPage, currentPage, pageGroup, handlePageGroup } = usePaginationStateHooks(hospitalData);

    useEffect(() => {
        adminManageHospitalApiCall();
    }, [currentPage]);

    const adminManageHospitalApiCall = async () => {
        setLoading(true);
        try {
            const response = await adminManageHospitalPageApi.getHospitalList(currentPage, searchValue, loginData.token);
            console.log('response', response.data.list);
            if (response.data) {
                setHospitalData(response.data.list);
                setTotalPages(response.data.pagination.totalPages);
                setTotalCount(response.data.pagination.totalCount);
            } else {
                console.log(response);
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };

    const adminManageHospitalUpdateApiCall = async () => {
        setLoading(true);
        try {
            if (editedHospital) {
                const response = await adminManageHospitalPageApi.updateHospital(editedHospital, loginData.token);
                if (response.data) {
                    setHospitalData(response.data.list);
                    setTotalPages(response.data.pagination.totalPages);
                    setTotalCount(response.data.pagination.totalCount);
                } else {
                    console.log(response);
                }
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };

    const handleResetButton = async () => {
        const response = await adminManageHospitalPageApi.getHospitalList(currentPage, null, loginData.token);
        setSearchValue('');
        setHospitalData(response.data.list);
        setPage(1);
        handlePageGroup(0);
    };

    const handleHospitalSelect = (hospital: GetHospitalListApiType) => {
        setSelectedHospital(hospital);
    };

    const handleAddHospital = () => {
        setIsNewHospital(true);
        setSelectedHospital({
            _id: '',
            name: '',
            tel: '',
            code: '',
            address: '',
            addressDetail: '',
            zipCode: '',
            phoneNumber: '',
            doctors: [],
            userCount: undefined,
            doctorCount: undefined,
            diagnosisCount: undefined,
            patientCount: undefined,
            completedAssessmentCount: undefined,
            totalAmount: undefined,
            bio: undefined,
            departments: undefined,
            images: undefined,
            lat: undefined,
            lng: undefined,
            timestamp: undefined
        });
    };

    const handleBack = () => {
        setSelectedHospital(null);
        setIsNewHospital(false);
    };

    return (
        <DiagnosisLayout>
            {selectedHospital ? (
                <AdminManageHospitalDetail 
                    hospital={selectedHospital} 
                    onBack={handleBack} 
                    onUpdate={adminManageHospitalApiCall}
                    isNew={isNewHospital}
                />
            ) : (
                <AdminInnerLayout>
                    <HospitalPageLabel onClick={() => window.location.reload()}>
                        {AdminManageHospitalConstants.pageCommonConstants.title}
                    </HospitalPageLabel>
                    <PageTitle $marginbottom='24px'>
                        {AdminManageHospitalConstants.pageCommonConstants.title}
                    </PageTitle>
                    <RowDivider $backgroundcolor={BorderColor.color_border_neutral} $marginbottom='16px' />
                    <FilterWrapper>
                        <AdminManageHospitalPageFilter 
                            diagnosisNumber={totalCount} 
                            onChange={setSearchValue} 
                            searchValue={searchValue} 
                            handleSearchFilter={adminManageHospitalApiCall}
                        >
                            <PositiveButton $height="32px" $width="80px" onClick={handleAddHospital}>등록</PositiveButton>
                        </AdminManageHospitalPageFilter>
                    </FilterWrapper>
                    <ContentLayout>
                        <AdminManageHospitalPagination
                            initNumber={hospitalData?.length}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            pageGroup={pageGroup}
                            handlePageGroup={handlePageGroup}
                            handlePageChange={setPage}
                            currentData={hospitalData}
                            totalCount={totalCount}
                            handleResetButton={handleResetButton}
                            onHospitalSelect={handleHospitalSelect}
                        />
                    </ContentLayout>
                </AdminInnerLayout>
            )}
        </DiagnosisLayout>
    );
};

export default AdminManageHospitalPage;

const ContentLayout = styled.div`
    width: 100%;
`;
